(() => {
  const initHeaderCapsuleMotion = () => {
    const headerSection = document.querySelector('.template-index .header-section');
    const header = headerSection?.querySelector('.header');
    if (!window.gsap || !header || header.dataset.capsuleMotionInitialized) return;

    header.dataset.capsuleMotionInitialized = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let isSticky = headerSection.classList.contains('header-scrolled');
    let isCapsule = false;
    let scrollFrame;

    gsap.set(header, {
      '--header-capsule-inset-x': '0px',
      '--header-capsule-inset-y': '0px',
      '--header-capsule-radius': '0px',
       '--header-capsule-border-opacity': 0
     });

    const capsuleTween = gsap.to(header, {
      '--header-capsule-inset-x': '64px',
      '--header-capsule-inset-y': '16px',
      '--header-capsule-radius': () => `${header.getBoundingClientRect().height / 2}px`,
      '--header-capsule-border-opacity': 0.14,
      duration: 0.45,
      ease: 'power3.inOut',
      paused: true,
      onReverseComplete: () => headerSection.classList.remove('header-capsule-transitioning')
    });

    const animateHeader = (capsule, immediate = false) => {
      if (window.innerWidth < 768) return;

      if (capsule === isCapsule && !immediate) return;
      isCapsule = capsule;

      if (immediate || reduceMotion) {
        capsuleTween.progress(capsule ? 1 : 0).pause();
        headerSection.classList.remove('header-capsule-transitioning');
        return;
      }

      if (capsule) {
        headerSection.classList.remove('header-capsule-transitioning');
        capsuleTween.play();
      }
      else {
        headerSection.classList.add('header-capsule-transitioning');
        capsuleTween.reverse();
      }
    };

    const updateCapsule = (immediate = false) => {
      animateHeader(isSticky, immediate);
    };

    document.addEventListener('header:scrolled', (event) => {
      isSticky = Boolean(event.detail?.scrolled);
      updateCapsule();
    });

    window.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        updateCapsule();
      });
    }, { passive: true });

    window.addEventListener('resize', () => {
      if (window.innerWidth < 768) {
        isCapsule = false;
        headerSection.classList.remove('header-capsule-transitioning');
        gsap.set(header, {
          clearProps: '--header-capsule-inset-x,--header-capsule-inset-y,--header-capsule-radius,--header-capsule-border-opacity'
        });
        return;
      }
      capsuleTween.invalidate();
      updateCapsule(true);
    });

    updateCapsule(true);
  };

  const initCustomSectionMotion = (root = document) => {
    initHorizontalReviews(root);
    initDesktopDragScroll(root);
    initSlideshowVideoControls(root);
    if (!window.gsap) return;

    initBrandIntroductionText(root);
    initMagneticButtons(root);
    initFeaturedProductsMotion(root);
    initReviewImageMotion(root);
    initImagePreviews(root);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    initHorizontalSections(root);
    initReviewFormations(root);

    root.querySelectorAll('.innovation-story__grid, .application-domains__grid').forEach((grid) => {
      if (grid.closest('[data-review-formation]')) return;
      if (grid.dataset.gsapInitialized) return;
      grid.dataset.gsapInitialized = 'true';

      const section = grid.closest('.shopify-section');
      const intro = section?.querySelector('[data-gsap-story-intro], [data-gsap-domain-intro]');
      const cards = [...grid.querySelectorAll('[data-gsap-story-card], [data-gsap-domain-card]')];
      const entranceCards = cards.filter((card) => !card.closest('[data-horizontal-scroll], [data-review-formation]'));

      gsap.set(intro, { y: 24, autoAlpha: 0 });
      gsap.set(entranceCards, { y: 30, autoAlpha: 0 });

      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;

        if (intro) {
          gsap.to(intro, {
            y: 0,
            autoAlpha: 1,
            duration: 0.75,
            ease: 'power3.out',
            clearProps: 'transform,opacity,visibility'
          });
        }

        gsap.to(entranceCards, {
          y: 0,
          autoAlpha: 1,
          duration: 0.7,
          stagger: 0.1,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility'
        });
        observer.disconnect();
      }, { threshold: 0.18 });

      observer.observe(grid);

      cards.forEach((card) => {
        if (card.closest('[data-horizontal-scroll], [data-review-formation]')) return;
        const image = card.querySelector('img');
        if (!image) return;

        card.addEventListener('mouseenter', () => {
          gsap.to(image, { scale: 1.035, duration: 0.45, ease: 'power2.out', overwrite: 'auto' });
        });
        card.addEventListener('mouseleave', () => {
          gsap.to(image, { scale: 1, duration: 0.45, ease: 'power2.out', overwrite: 'auto' });
        });
      });
    });
  };

  const initBrandIntroductionText = (root = document) => {
    if (!window.gsap || !window.ScrollTrigger) return;

    const sections = root.matches?.('[data-brand-introduction]')
      ? [root, ...root.querySelectorAll('[data-brand-introduction]')]
      : root.querySelectorAll('[data-brand-introduction]');

    window.gsap.registerPlugin(window.ScrollTrigger);

    sections.forEach((section) => {
      if (section.dataset.brandTextInitialized) return;
      section.dataset.brandTextInitialized = 'true';

      const targets = [
        section.querySelector('.title-wrapper > .grid > .heading'),
        section.querySelector('.title-wrapper > .grid > .description p')
      ].filter(Boolean);

      if (!targets.length) return;

      const words = [];
      const highlightedWords = new Set(['vision', 'displays']);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      targets.forEach((target) => {
        const text = target.textContent.trim();
        const accessibleText = document.createElement('span');
        const visualText = document.createElement('span');
        const textColor = window.getComputedStyle(target).color;
        const gradient = `linear-gradient(90deg, ${textColor} 0%, ${textColor} 30%, #ff0099 35%, #ff0000 45%, #ff4f04 50%, #ffa600 55%, #f8f8f8 60%, #0056ff 65%, #ffffff 70%, #ffffff 100%)`;

        accessibleText.className = 'sr-only';
        accessibleText.textContent = text;
        visualText.className = 'brand-sweep-visual';
        visualText.setAttribute('aria-hidden', 'true');

        text.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            visualText.append(document.createTextNode(part));
            return;
          }

          const word = document.createElement('span');
          const base = document.createElement('span');
          const overlay = document.createElement('span');
          const normalizedWord = part.toLowerCase().replace(/[^a-z0-9]/g, '');
          const highlight = highlightedWords.has(normalizedWord) ? document.createElement('span') : null;
          word.className = 'brand-sweep-word';
          base.className = 'brand-sweep-base';
          if (highlight) {
            highlight.className = 'brand-sweep-highlight';
            highlight.textContent = part;
          }
          overlay.className = 'brand-sweep-overlay';
          base.textContent = part;
          overlay.textContent = part;
          overlay.style.backgroundImage = gradient;
          word.append(base, ...(highlight ? [highlight] : []), overlay);
          visualText.append(word);
          words.push({ base, overlay, highlight });
        });

        target.replaceChildren(accessibleText, visualText);
      });

      if (reduceMotion) {
        words.forEach(({ base, overlay, highlight }) => {
          base.style.opacity = '1';
          overlay.hidden = true;
          if (highlight) highlight.style.opacity = '1';
        });
        return;
      }

      const timeline = window.gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 90%',
          once: true
        }
      });

      words.forEach(({ base, overlay }, index) => {
        const start = index * 0.12;
        timeline.fromTo(overlay, {
          backgroundPosition: '100% 0',
          opacity: 1,
          filter: 'blur(4px)'
        }, {
          backgroundPosition: '0% 0',
          filter: 'blur(0px)',
          duration: 0.75,
          ease: 'power2.inOut'
        }, start);
        timeline.set(base, { opacity: 1 }, start + 0.6);
        timeline.to(overlay, { opacity: 0, duration: 0.05, ease: 'none' }, start + 0.7);
      });

      const highlights = words.map(({ highlight }) => highlight).filter(Boolean);
      const sweepEnd = Math.max(0, (words.length - 1) * 0.12 + 0.75);
      timeline.to(highlights, {
        opacity: 1,
        duration: 1.5,
        stagger: 0.2,
        ease: 'power2.out'
      }, sweepEnd + 0.2);
    });
  };

  const initSlideshowVideoControls = (root = document) => {
    const slideshows = root.matches?.('.slideshow-section')
      ? [root]
      : root.querySelectorAll('.slideshow-section');

    slideshows.forEach((section) => {
      if (section.dataset.videoControlsInitialized) return;

      const controls = [...section.querySelectorAll('[data-slideshow-video-control]')];
      const modal = section.querySelector('[data-slideshow-video-modal]');
      const viewport = modal?.querySelector('[data-slideshow-video-modal-viewport]');
      if (!controls.length || !modal || !viewport) return;

      section.dataset.videoControlsInitialized = 'true';
      const dialog = modal.querySelector('.slideshow-video-modal__dialog');
      const closeTriggers = [...modal.querySelectorAll('[data-slideshow-video-modal-close]')];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const animation = window.gsap;
      let activeMedia = null;
      let originalParent = null;
      let originalNextSibling = null;
      let activeSource = null;
      let activeTrigger = null;
      let isOpen = false;
      let lockedScrollY = 0;
      let previousHtmlOverflow = '';
      let previousBodyOverflow = '';
      let previousBodyPosition = '';
      let previousBodyTop = '';
      let previousBodyWidth = '';

      animation?.set(modal, { autoAlpha: 0 });

      const setSlideshowPlayback = (eventName) => {
        section.querySelector('slideshow-element')?.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
      };

      const lockPageScroll = () => {
        lockedScrollY = window.scrollY;
        previousHtmlOverflow = document.documentElement.style.overflow;
        previousBodyOverflow = document.body.style.overflow;
        previousBodyPosition = document.body.style.position;
        previousBodyTop = document.body.style.top;
        previousBodyWidth = document.body.style.width;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedScrollY}px`;
        document.body.style.width = '100%';
        document.body.classList.add('slideshow-video-modal-open');
        window.desviewLenis?.stop();
        document.dispatchEvent(new CustomEvent('modal:afterShow'));
      };

      const unlockPageScroll = () => {
        document.body.classList.remove('slideshow-video-modal-open');
        document.documentElement.style.overflow = previousHtmlOverflow;
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.position = previousBodyPosition;
        document.body.style.top = previousBodyTop;
        document.body.style.width = previousBodyWidth;
        window.scrollTo(0, lockedScrollY);
        document.dispatchEvent(new CustomEvent('modal:afterHide'));
      };

      const restoreMedia = () => {
        if (!activeMedia || !originalParent) return;
        if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
          originalParent.insertBefore(activeMedia, originalNextSibling);
        }
        else {
          originalParent.append(activeMedia);
        }
        if (activeSource) {
          activeSource.classList.remove('is-playing');
          activeSource.setAttribute('aria-hidden', 'true');
        }
        activeMedia = null;
        originalParent = null;
        originalNextSibling = null;
        activeSource = null;
      };

      const closeModal = () => {
        if (!isOpen) return;
        isOpen = false;
        activeMedia?.pause();
        setSlideshowPlayback('slider:play');
        unlockPageScroll();
        modal.classList.remove('is-open');
        const finish = () => {
          restoreMedia();
          modal.setAttribute('aria-hidden', 'true');
          if (modal.open) modal.close();
          dialog?.setAttribute('tabindex', '-1');
          activeTrigger?.focus({ preventScroll: true });
          activeTrigger = null;
        };
        if (!animation || reduceMotion) {
          animation?.set(modal, { autoAlpha: 0 });
          finish();
          return;
        }
        animation.timeline({ onComplete: finish })
          .to(dialog, { y: 18, scale: .97, autoAlpha: 0, duration: .24, ease: 'power2.in' }, 0)
          .to(modal, { autoAlpha: 0, duration: .24, ease: 'power2.in' }, 0);
      };

      const openModal = async (control) => {
        if (isOpen) return;
        const index = control.dataset.slideIndex;
        const slide = section.querySelector(`[data-slide-index="${index}"]`);
        const mediaElements = [...(slide?.querySelectorAll('video-media') || [])];
        const media = mediaElements.find((item) => getComputedStyle(item).display !== 'none') || mediaElements[0];
        if (!media) return;

        setSlideshowPlayback('slider:pause');
        activeMedia = media;
        activeTrigger = control;
        originalParent = media.parentNode;
        originalNextSibling = media.nextSibling;
        activeSource = media.closest('[data-slideshow-video-source]');
        viewport.append(media);
        activeSource?.classList.add('is-playing');
        activeSource?.removeAttribute('aria-hidden');
        if (!modal.open) modal.showModal();
        modal.setAttribute('aria-hidden', 'false');
        modal.classList.add('is-open');
        lockPageScroll();
        isOpen = true;

        const playPromise = media.play();
        if (playPromise?.catch) await playPromise.catch(() => {});
        dialog?.focus({ preventScroll: true });

        if (!animation || reduceMotion) {
          animation?.set(modal, { autoAlpha: 1 });
          animation?.set(dialog, { clearProps: 'all' });
          return;
        }
        animation.timeline()
          .set(modal, { autoAlpha: 1 })
          .fromTo(dialog, { y: 18, scale: .97, autoAlpha: 0 }, { y: 0, scale: 1, autoAlpha: 1, duration: .42, ease: 'power3.out' });
      };

      controls.forEach((control) => {
        control.addEventListener('click', () => openModal(control));
      });

      closeTriggers.forEach((trigger) => trigger.addEventListener('click', closeModal));
      modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeModal();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen) closeModal();
      });
      section.addEventListener('shopify:section:unload', () => {
        activeMedia?.pause();
        restoreMedia();
        if (isOpen) unlockPageScroll();
        if (modal.open) modal.close();
      }, { once: true });
    });
  };

  const initFeaturedProductsMotion = (root = document) => {
    if (!window.gsap || !window.ScrollTrigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    window.gsap.registerPlugin(window.ScrollTrigger);

    const sections = root.matches?.('[data-featured-products-motion]')
      ? [root]
      : root.querySelectorAll('[data-featured-products-motion]');

    sections.forEach((section) => {
      if (section.dataset.featuredProductsInitialized) return;

      const viewport = section.querySelector('[data-featured-products-viewport]');
      const track = section.querySelector('[data-featured-products-track]');
      const cards = [...section.querySelectorAll('[data-featured-product-card]')];
      if (!viewport || !track || !cards.length) return;

      section.dataset.featuredProductsInitialized = 'true';
      const middleIndex = (cards.length - 1) / 2;

      gsap.matchMedia().add('(min-width: 768px)', () => {
        const getDistance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
        const resetTrack = () => gsap.set(track, { x: 0 });
        resetTrack();

        const timeline = gsap.timeline({
          defaults: { ease: 'power3.out' },
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: () => `+=${Math.max(window.innerHeight * 2.5, getDistance() + window.innerHeight)}`,
            pin: true,
            scrub: 0.5,
            invalidateOnRefresh: true,
            onRefreshInit: resetTrack,
            onEnter: resetTrack,
            onEnterBack: resetTrack
          }
        });

        timeline.from(cards, {
          y: () => window.innerHeight,
          autoAlpha: 0,
          rotation: (index) => {
            const distance = Math.abs(index - middleIndex);
            return index < middleIndex ? distance * 2 : distance * -2;
          },
          transformOrigin: '50% 0%',
          stagger: { amount: 0.35, from: 'center' }
        });

        timeline.to(track, {
          x: () => -getDistance(),
          ease: 'none',
          duration: 2.4
        }, 0.35);

        return () => {
          timeline.kill();
          gsap.set([...cards, track], { clearProps: 'transform,opacity,visibility' });
        };
      });
    });
  };

  const initReviewFormations = (root = document) => {
    if (!window.gsap || !window.ScrollTrigger) return;
    window.gsap.registerPlugin(window.ScrollTrigger);

    const sections = root.matches?.('[data-review-formation]')
      ? [root, ...root.querySelectorAll('[data-review-formation]')]
      : root.querySelectorAll('[data-review-formation]');

    sections.forEach((section) => {
      if (section.dataset.gsapReviewInitialized) return;

      const grid = section.querySelector('[data-review-grid]');
      const images = [...section.querySelectorAll('[data-review-image]')];
      const copy = [...section.querySelectorAll('[data-review-copy]')];
      if (!grid || !images.length) return;

      section.dataset.gsapReviewInitialized = 'true';
      const middleIndex = Math.floor(images.length / 2);

      gsap.matchMedia().add('(min-width: 768px)', () => {
        const timeline = gsap.timeline({
          defaults: { ease: 'sine.out' },
          scrollTrigger: {
            trigger: grid,
            start: 'top top',
            end: '+=250%',
            pin: grid.parentNode,
            pinSpacing: true,
            scrub: 0.5,
            invalidateOnRefresh: true
          }
        });

        timeline.from(images, {
          stagger: {
            amount: 0.3,
            from: 'center'
          },
          y: () => window.innerHeight * 0.55,
          transformOrigin: '50% 0%',
          rotation: (index) => {
            const distanceFromCenter = Math.abs(index - middleIndex);
            return index < middleIndex ? distanceFromCenter * 3 : distanceFromCenter * -3;
          }
        });

        if (copy.length) {
          timeline.from(copy, {
            stagger: {
              amount: 0.3,
              from: 'center'
            },
            yPercent: 100,
            autoAlpha: 0
          }, 0);
        }

        return () => {
          timeline.kill();
          gsap.set([...images, ...copy], { clearProps: 'transform,opacity,visibility' });
        };
      });
    });
  };

  const initHorizontalReviews = (root = document) => {
    const sections = root.matches?.('[data-horizontal-reviews]')
      ? [root, ...root.querySelectorAll('[data-horizontal-reviews]')]
      : root.querySelectorAll('[data-horizontal-reviews]');

    sections.forEach((section) => {
      const viewport = section.querySelector('[data-horizontal-viewport]');
      const track = section.querySelector('[data-horizontal-track]');
      if (!viewport || !track || section.dataset.reviewScrollerInitialized) return;

      section.dataset.reviewScrollerInitialized = 'true';
      viewport.classList.add('reviews-horizontal--draggable');
      viewport.querySelectorAll('img').forEach((image) => image.draggable = false);

      const horizontalLenis = window.Lenis ? new window.Lenis({
        wrapper: viewport,
        content: track,
        eventsTarget: document.createElement('div'),
        orientation: 'horizontal',
        gestureOrientation: 'both',
        duration: 0.75,
        smoothWheel: true,
        syncTouch: false,
        overscroll: true,
        autoRaf: true
      }) : null;

      let pointerId = null;
      let lastX = 0;
      let dragTarget = 0;
      let isDragging = false;
      let suppressClick = false;
      const previousButton = section.querySelector('[data-reviews-previous]');
      const nextButton = section.querySelector('[data-reviews-next]');

      const updateNavigation = () => {
        const maxScroll = horizontalLenis?.limit ?? viewport.scrollWidth - viewport.clientWidth;
        const currentScroll = horizontalLenis?.animatedScroll ?? viewport.scrollLeft;
        if (previousButton) previousButton.disabled = currentScroll <= 1;
        if (nextButton) nextButton.disabled = currentScroll >= maxScroll - 1;
      };

      const moveByCard = (direction) => {
        const card = track.querySelector('[data-review-card]');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        const currentScroll = horizontalLenis?.targetScroll ?? viewport.scrollLeft;
        const maxScroll = horizontalLenis?.limit ?? viewport.scrollWidth - viewport.clientWidth;
        const targetScroll = Math.max(0, Math.min(maxScroll, currentScroll + direction * (card.getBoundingClientRect().width + gap)));
        if (horizontalLenis) horizontalLenis.scrollTo(targetScroll, { duration: 0.5 });
        else viewport.scrollTo({ left: targetScroll, behavior: 'smooth' });
      };

      previousButton?.addEventListener('click', () => moveByCard(-1));
      nextButton?.addEventListener('click', () => moveByCard(1));
      viewport.addEventListener('scroll', updateNavigation, { passive: true });
      horizontalLenis?.on('scroll', updateNavigation);
      window.addEventListener('resize', updateNavigation);
      updateNavigation();

      viewport.addEventListener('pointerdown', (event) => {
        const interactive = event.target.closest('a, button, input, select, textarea, video, iframe, [role="button"], [contenteditable]');
        if (event.pointerType !== 'mouse' || event.button !== 0 || (interactive && !interactive.matches('[data-image-preview-trigger]'))) return;
        pointerId = event.pointerId;
        lastX = event.clientX;
        dragTarget = horizontalLenis?.targetScroll ?? viewport.scrollLeft;
        isDragging = false;
      });

      viewport.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const delta = event.clientX - lastX;
        if (!isDragging && Math.abs(delta) < 3) return;

        if (!isDragging) viewport.setPointerCapture(pointerId);
        isDragging = true;
        lastX = event.clientX;
        const maxScroll = horizontalLenis?.limit ?? viewport.scrollWidth - viewport.clientWidth;
        dragTarget = Math.max(0, Math.min(maxScroll, dragTarget - delta));
        viewport.classList.add('is-dragging');
        if (horizontalLenis) {
          horizontalLenis.scrollTo(dragTarget, { duration: 0.25 });
        } else {
          viewport.scrollLeft = dragTarget;
        }
        event.preventDefault();
      });

      const releasePointer = (event) => {
        if (event.pointerId !== pointerId) return;
        if (viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
        suppressClick = isDragging;
        pointerId = null;
        isDragging = false;
        viewport.classList.remove('is-dragging');
        window.setTimeout(() => suppressClick = false, 0);
      };

      viewport.addEventListener('pointerup', releasePointer);
      viewport.addEventListener('pointercancel', releasePointer);
      viewport.addEventListener('dragstart', (event) => event.preventDefault());
      viewport.addEventListener('click', (event) => {
        if (!suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
      section.addEventListener('shopify:section:unload', () => {
        window.removeEventListener('resize', updateNavigation);
        horizontalLenis?.off('scroll', updateNavigation);
        horizontalLenis?.destroy();
      }, { once: true });
    });
  };

  const initReviewImageMotion = (root = document) => {
    if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const mediaItems = root.matches?.('.reviews-horizontal__image')
      ? [root, ...root.querySelectorAll('.reviews-horizontal__image')]
      : root.querySelectorAll('.reviews-horizontal__image');

    mediaItems.forEach((media) => {
      const image = media.querySelector('img');
      if (!image || media.dataset.reviewImageMotionInitialized) return;

      media.dataset.reviewImageMotionInitialized = 'true';
      const moveX = gsap.quickTo(image, 'xPercent', { duration: 0.35, ease: 'power3.out' });
      const moveY = gsap.quickTo(image, 'yPercent', { duration: 0.35, ease: 'power3.out' });

      media.addEventListener('pointerenter', () => {
        if (media.classList.contains('is-shared-image-preview')) return;
        gsap.set(image, { willChange: 'transform' });
        gsap.to(image, {
          scale: 1.09,
          duration: 0.5,
          ease: 'power3.out',
          overwrite: 'auto'
        });
      });

      media.addEventListener('pointermove', (event) => {
        if (media.classList.contains('is-shared-image-preview')) return;
        const bounds = media.getBoundingClientRect();
        const pointerX = (event.clientX - bounds.left) / bounds.width - 0.5;
        const pointerY = (event.clientY - bounds.top) / bounds.height - 0.5;

        moveX(pointerX * -6);
        moveY(pointerY * -6);
      });

      media.addEventListener('pointerleave', () => {
        moveX(0);
        moveY(0);
        gsap.to(image, {
          scale: 1,
          duration: 0.55,
          ease: 'power3.out',
          overwrite: 'auto',
          onComplete: () => gsap.set(image, { clearProps: 'willChange' })
        });
      });
    });
  };

  const initImagePreviews = (root = document) => {
    if (!window.gsap) return;

    const sections = root.matches?.('[data-image-preview-section]')
      ? [root, ...root.querySelectorAll('[data-image-preview-section]')]
      : root.querySelectorAll('[data-image-preview-section]');

    sections.forEach((section) => {
      if (section.dataset.imagePreviewInitialized) return;

      const overlay = section.querySelector('[data-image-preview-overlay]');
      const triggers = [...section.querySelectorAll('[data-image-preview-trigger]')];
      if (!overlay || !triggers.length) return;

      section.dataset.imagePreviewInitialized = 'true';
      document.body.append(overlay);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reduceMotion ? 0 : 0.5;
      let activeTrigger = null;
      let placeholder = null;
      let originalStyle = null;
      let isOpen = false;

      const finishClose = () => {
        if (placeholder?.isConnected && activeTrigger) placeholder.replaceWith(activeTrigger);
        if (activeTrigger) {
          activeTrigger.classList.remove('is-shared-image-preview');
          if (originalStyle === null) activeTrigger.removeAttribute('style');
          else activeTrigger.setAttribute('style', originalStyle);
        }
        overlay.setAttribute('aria-hidden', 'true');
        gsap.set(overlay, { autoAlpha: 0, clearProps: 'backgroundColor' });
        document.body.classList.remove('shared-image-preview-open');
        activeTrigger?.focus({ preventScroll: true });
        activeTrigger = null;
        placeholder = null;
        originalStyle = null;
        isOpen = false;
      };

      const closePreview = (immediate = false) => {
        if (!isOpen || !activeTrigger || !placeholder) return;

        const sourceBounds = placeholder.getBoundingClientRect();
        const closeDuration = immediate || reduceMotion ? 0 : 0.4;

        if (immediate) {
          finishClose();
          return;
        }

        gsap.timeline({ onComplete: finishClose })
          .to(overlay, { backgroundColor: 'rgba(0, 0, 0, 0)', duration: closeDuration, ease: 'power2.out' }, 0)
          .to(activeTrigger, {
            top: sourceBounds.top,
            left: sourceBounds.left,
            width: sourceBounds.width,
            height: sourceBounds.height,
            duration: closeDuration,
            ease: 'power3.inOut'
          }, 0);
      };

      const openPreview = (trigger) => {
        if (isOpen) return;
        const source = trigger.querySelector('img');
        if (!source) return;

        const sourceBounds = trigger.getBoundingClientRect();
        const imageRatio = source.naturalWidth && source.naturalHeight
          ? source.naturalWidth / source.naturalHeight
          : sourceBounds.width / sourceBounds.height;
        const maxWidth = window.innerWidth * 0.9;
        const maxHeight = window.innerHeight * 0.88;
        const targetWidth = Math.min(maxWidth, maxHeight * imageRatio);
        const targetHeight = targetWidth / imageRatio;

        activeTrigger = trigger;
        isOpen = true;
        trigger.dispatchEvent(new Event('pointerleave'));
        originalStyle = trigger.getAttribute('style');
        placeholder = document.createElement('div');
        placeholder.className = `${trigger.className} shared-image-preview__placeholder`;
        placeholder.setAttribute('aria-hidden', 'true');
        trigger.replaceWith(placeholder);
        overlay.append(trigger);
        trigger.classList.add('is-shared-image-preview');

        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('shared-image-preview-open');
        gsap.set(overlay, { visibility: 'visible', opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0)' });
        gsap.set(trigger, {
          top: sourceBounds.top,
          left: sourceBounds.left,
          width: sourceBounds.width,
          height: sourceBounds.height
        });

        gsap.to(overlay, { backgroundColor: 'rgba(0, 0, 0, .94)', duration: reduceMotion ? 0 : 0.3, ease: 'power2.out' });
        gsap.to(trigger, {
          top: (window.innerHeight - targetHeight) / 2,
          left: (window.innerWidth - targetWidth) / 2,
          width: targetWidth,
          height: targetHeight,
          duration,
          ease: 'power3.inOut'
        });
      };

      const handleKeydown = (event) => {
        if (event.key === 'Escape' && isOpen) closePreview();
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          openPreview(trigger);
        });
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openPreview(trigger);
        });
      });

      overlay.addEventListener('click', (event) => {
        if (event.target.closest('[data-image-preview-trigger]')) return;
        closePreview();
      });
      document.addEventListener('keydown', handleKeydown);
      section.addEventListener('shopify:section:unload', () => {
        closePreview(true);
        document.removeEventListener('keydown', handleKeydown);
        overlay.remove();
      }, { once: true });
    });
  };

  const initDesktopDragScroll = (root = document) => {
    const scrollers = root.matches?.('[data-desktop-drag-scroll]')
      ? [root, ...root.querySelectorAll('[data-desktop-drag-scroll]')]
      : root.querySelectorAll('[data-desktop-drag-scroll]');

    scrollers.forEach((scroller) => {
      if (scroller.dataset.desktopDragInitialized) return;
      scroller.dataset.desktopDragInitialized = 'true';
      scroller.querySelectorAll('img').forEach((image) => image.draggable = false);

      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let didDrag = false;

      scroller.addEventListener('pointerdown', (event) => {
        if (window.innerWidth < 1024 || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input, select, textarea, video, iframe, [role="button"], [contenteditable]')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = scroller.scrollLeft;
        didDrag = false;
        scroller.setPointerCapture(pointerId);
      });

      scroller.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const distance = event.clientX - startX;
        if (!didDrag && Math.abs(distance) < 4) return;
        didDrag = true;
        scroller.classList.add('is-dragging');
        scroller.scrollLeft = startScrollLeft - distance;
        event.preventDefault();
      });

      const releasePointer = (event) => {
        if (event.pointerId !== pointerId) return;
        if (scroller.hasPointerCapture(pointerId)) scroller.releasePointerCapture(pointerId);
        pointerId = null;
        scroller.classList.remove('is-dragging');
      };

      scroller.addEventListener('pointerup', releasePointer);
      scroller.addEventListener('pointercancel', releasePointer);
      scroller.addEventListener('dragstart', (event) => event.preventDefault());
      scroller.addEventListener('click', (event) => {
        if (!didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        didDrag = false;
      }, true);
    });
  };

  const initHorizontalSections = (root = document) => {
    if (!window.gsap || !window.ScrollTrigger) return;
    window.gsap.registerPlugin(window.ScrollTrigger);

    const sections = root.matches?.('[data-horizontal-scroll]')
      ? [root, ...root.querySelectorAll('[data-horizontal-scroll]')]
      : root.querySelectorAll('[data-horizontal-scroll]');

    sections.forEach((section) => {
      const viewport = section.querySelector('[data-horizontal-viewport]');
      const track = section.querySelector('[data-horizontal-track]');
      if (!viewport || !track || section.matches('[data-horizontal-reviews]') || section.dataset.gsapHorizontalInitialized) return;

      section.dataset.gsapHorizontalInitialized = 'true';
      const getDistance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
      const isReverse = section.dataset.horizontalDirection === 'reverse';
      let horizontalTween;

      const createHorizontalTween = () => {
        const setTrackStart = () => {
          gsap.set(track, { x: isReverse ? -getDistance() : 0 });
        };

        setTrackStart();
        horizontalTween = gsap.to(track, {
          x: () => isReverse ? 0 : -getDistance(),
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'center center',
            pin: true,
            pinSpacing: true,
            scrub: true,
            end: () => `+=${getDistance()}`,
            invalidateOnRefresh: true,
            onRefreshInit: setTrackStart
          }
        });

        track.querySelectorAll('.innovation-story__image, .application-domains__image').forEach((image) => {
            gsap.fromTo(image, { xPercent: isReverse ? 8 : -8 }, {
              xPercent: isReverse ? -8 : 8,
              ease: 'none',
              scrollTrigger: {
                trigger: image,
                containerAnimation: horizontalTween,
                start: 'left right',
                end: 'right left',
                scrub: true,
                invalidateOnRefresh: true
              }
            });
        });
      };

      if (track.matches('.innovation-story__grid')) {
        gsap.matchMedia().add('(min-width: 768px)', createHorizontalTween);
      } else {
        createHorizontalTween();
      }

    });
  };

  const initMagneticButtons = (root = document) => {
    if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    root.querySelectorAll('[data-gsap-magnetic-zone]').forEach((zone) => {
      if (zone.dataset.gsapMagneticInitialized) return;

      const button = zone.querySelector('.button, .link');
      const label = button?.querySelector('.btn-text, .link-text');
      if (!button) return;

      zone.dataset.gsapMagneticInitialized = 'true';
      zone.addEventListener('mousemove', (event) => {
        const rect = zone.getBoundingClientRect();
        const offsetX = event.clientX - rect.left - rect.width / 2;
        const offsetY = event.clientY - rect.top - rect.height / 2;

        gsap.to(button, {
          x: offsetX * 0.4,
          y: offsetY * 0.4,
          duration: 0.4,
          ease: 'power2.out',
          overwrite: 'auto'
        });

        if (label) {
          gsap.to(label, {
            x: offsetX * 0.24,
            y: offsetY * 0.24,
            duration: 0.4,
            ease: 'power2.out',
            overwrite: 'auto'
          });
        }
      });

      zone.addEventListener('mouseleave', () => {
        gsap.to(button, {
          x: 0,
          y: 0,
          duration: 0.7,
          ease: 'elastic.out(1, 0.4)',
          overwrite: 'auto'
        });

        if (label) {
          gsap.to(label, {
            x: 0,
            y: 0,
            duration: 0.7,
            ease: 'elastic.out(1, 0.4)',
            overwrite: 'auto'
          });
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initHeaderCapsuleMotion();
      initCustomSectionMotion();
    }, { once: true });
  } else {
    initHeaderCapsuleMotion();
    initCustomSectionMotion();
  }

  document.addEventListener('shopify:section:load', (event) => initCustomSectionMotion(event.target));
})();
